import { APP_LOCALES, type AppLocale } from "@/i18n/locales";

import type { ThreatReportSummary } from "./cors-edge-lambda-api";

const LOCALE_PREFIX: Record<AppLocale, string> = {
  en: "en",
  el: "el",
  es: "es",
  de: "de",
};

/** Names the assistant stored before it normalized them to BCP-47. */
const LEGACY_LANGUAGE_NAMES: Record<AppLocale, readonly string[]> = {
  en: ["english"],
  el: ["greek"],
  es: ["spanish"],
  de: ["german"],
};

export function responseLanguageOf(row: object | null | undefined): string | null {
  if (!row) return null;
  const record = row as Record<string, unknown>;
  const value = record.response_language ?? record.responseLanguage;
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** A missing language is an English report written before language selection. */
export function reportLanguageMatchesLocale(
  responseLanguage: string | null | undefined,
  locale: AppLocale,
): boolean {
  const raw = responseLanguage?.trim().toLowerCase() ?? "";
  if (!raw) return locale === "en";
  const prefix = LOCALE_PREFIX[locale];
  if (raw === prefix || raw.startsWith(`${prefix}-`)) return true;
  return LEGACY_LANGUAGE_NAMES[locale].includes(raw);
}

/**
 * Locale a stored report language belongs to.
 * A missing language is English. A tag this app does not offer is null.
 */
export function localeOfReportLanguage(responseLanguage: string | null | undefined): AppLocale | null {
  for (const locale of APP_LOCALES) {
    if (reportLanguageMatchesLocale(responseLanguage, locale)) return locale;
  }
  return null;
}

/** Two reports were generated in the same language. A missing language is English. */
export function sameThreatReportLanguage(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const leftLocale = localeOfReportLanguage(left);
  const rightLocale = localeOfReportLanguage(right);
  if (leftLocale != null || rightLocale != null) return leftLocale === rightLocale;
  const normalize = (value: string | null | undefined) => value?.trim().toLowerCase() ?? "";
  return normalize(left) === normalize(right);
}

/**
 * Newest completed report generated in ``locale``.
 * The list is newest-first. An in-progress row has no language until it finishes.
 * A missing language is a legacy English report, so callers that still need to
 * read ``response_language`` off the stored row must do that before this.
 */
export function threatReportForLocale<T extends Pick<ThreatReportSummary, "status" | "responseLanguage">>(
  reports: readonly T[],
  locale: AppLocale,
): T | null {
  return (
    reports.find(
      (report) =>
        report.status === "completed" && reportLanguageMatchesLocale(report.responseLanguage, locale),
    ) ?? null
  );
}

type LoadReportRow<T extends ThreatReportSummary> = {
  (report: T): Promise<object | null>;
};

/**
 * Copy ``response_language`` from each stored row when the version list did
 * not include it. A list that already names at least one language is unchanged.
 */
export async function hydrateReportLanguages<T extends ThreatReportSummary>(
  reports: readonly T[],
  loadRow: LoadReportRow<T>,
): Promise<T[]> {
  const languageKnown = reports.some(
    (report) => typeof report.responseLanguage === "string" && report.responseLanguage.trim() !== "",
  );
  if (languageKnown) return [...reports];
  return Promise.all(
    reports.map(async (report) => {
      if (report.status !== "completed") return report;
      try {
        const responseLanguage = responseLanguageOf(await loadRow(report));
        return responseLanguage ? { ...report, responseLanguage } : report;
      } catch {
        return report;
      }
    }),
  );
}
